/**
 * ContactsCard (TASK-208)
 *
 * Dashboard block listing recent client dialogs (contacts).
 * Clicking a contact opens a read-only transcript modal.
 * KAN-710: through ?dialog=<clientId>&estimate=<id> (or ?estimate=<id> for a
 * contact without a client); TranscriptDialogHost renders the modal.
 */
import { MessageSquare } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { Contact } from '@/services/api/dashboard'
import { SenderAccountLink } from '@/components/messages/SenderAccountLink'
import { ChannelBadge } from './ChannelBadge'
import { useDialogParam } from '@/hooks/useDialogParam'

interface ContactsCardProps {
  contacts: Contact[]
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function contactTitle(contact: Contact): string {
  return (
    contact.client?.name ||
    contact.client?.phone ||
    contact.display_name ||
    'Клиент'
  )
}

const AVATAR_COLORS = [
  'bg-blue-500',
  'bg-emerald-500',
  'bg-violet-500',
  'bg-amber-500',
  'bg-rose-500',
  'bg-teal-500',
  'bg-indigo-500',
  'bg-orange-500',
]

function getInitials(contact: Contact): string {
  const name = contact.client?.name
  if (name) {
    const words = name.trim().split(/\s+/)
    return words
      .slice(0, 2)
      .map((w) => w[0])
      .join('')
      .toUpperCase()
  }
  return contact.display_name.slice(0, 2).toUpperCase()
}

function getAvatarColor(str: string): string {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

export function ContactsCard({ contacts }: ContactsCardProps) {
  const { openDialog } = useDialogParam()
  const openContact = (contact: Contact) =>
    openDialog({
      clientId: contact.client?.id ?? null,
      estimateId: contact.id,
      hint: {
        title: contactTitle(contact),
        manualMode: contact.client?.manual_mode,
      },
    })

  return (
    <Card>
      <CardHeader>
        <CardTitle>Контакты</CardTitle>
      </CardHeader>
      <CardContent>
        {contacts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-gray-400">
            <MessageSquare className="mb-2 h-8 w-8" />
            <p className="text-sm">Пока нет диалогов с клиентами</p>
          </div>
        ) : (
          <div className="space-y-1">
            {contacts.map((contact) => (
              // A div, not a <button>: the row holds the account link (KAN-645),
              // and a link inside a button is invalid markup.
              <div
                key={contact.id}
                role="button"
                tabIndex={0}
                data-testid={`contact-row-${contact.id}`}
                onClick={() => openContact(contact)}
                onKeyDown={(event) => {
                  if (event.target !== event.currentTarget) return
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    openContact(contact)
                  }
                }}
                className="flex w-full cursor-pointer items-start justify-between gap-3 rounded-md border-b px-2 py-3 text-left last:border-0 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              >
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${getAvatarColor(contactTitle(contact))}`}
                >
                  <span className="text-sm font-semibold text-white">
                    {getInitials(contact)}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate font-medium text-gray-900">
                      {contactTitle(contact)}
                    </p>
                    {contact.last_message && (
                      <ChannelBadge source={contact.last_message.source} />
                    )}
                    {contact.sender_account && (
                      <SenderAccountLink
                        account={contact.sender_account}
                        testId={`contact-sender-${contact.id}`}
                        className="shrink-0 text-gray-600"
                      />
                    )}
                  </div>
                  {contact.client?.phone && (
                    <p className="text-xs text-gray-500">
                      {contact.client.phone}
                    </p>
                  )}
                  {contact.last_message && (
                    <p className="mt-0.5 truncate text-sm text-gray-600">
                      {contact.last_message.content}
                    </p>
                  )}
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-xs text-gray-400">
                    {formatDate(contact.updated_at)}
                  </p>
                  <p className="mt-1 flex items-center justify-end gap-1 text-xs text-gray-500">
                    <MessageSquare className="h-3 w-3" />
                    {contact.messages_count}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
